<template>
    <Head title="Incident Performance" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Incident Performance</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Monitor incident SLA compliance and performance metrics</p>
                        </div>
                        <InfoButton title="About This Dashboard" variant="default">
                            <div class="space-y-3">
                                <div>
                                    <h4 class="font-semibold text-gray-900 mb-1">Incident SLA Performance Dashboard</h4>
                                    <p>This dashboard provides real-time insights into Service Level Agreement (SLA) compliance for your incidents.</p>
                                </div>

                                <div>
                                    <h4 class="font-semibold text-gray-900 mb-1">What You'll Find Here:</h4>
                                    <ul class="list-disc ml-5 space-y-1">
                                        <li><strong>Summary Metrics:</strong> Overview of active incidents, breached SLAs, at-risk items, and overall response/investigation performance</li>
                                        <li><strong>Status Categories:</strong> Incidents grouped by SLA status (Breached, On Track, Warning, Critical)</li>
                                        <li><strong>Performance Analysis:</strong> Detailed breakdowns by severity, incident type, and SLA policy</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 class="font-semibold text-gray-900 mb-1">Data Scope:</h4>
                                    <ul class="list-disc ml-5 space-y-1">
                                        <li><strong>Active Incidents:</strong> Shows only incidents not in closed status</li>
                                        <li><strong>Performance Metrics:</strong> Based on incidents closed in the last 90 days</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 class="font-semibold text-gray-900 mb-1">Tip:</h4>
                                    <p>Click the <strong>Refresh</strong> button to update all metrics with the latest data.</p>
                                </div>
                            </div>
                        </InfoButton>
                    </div>
                    <button
                        @click="refreshDashboard"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 focus:bg-blue-700 dark:focus:bg-blue-600 active:bg-blue-900 dark:active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:ring-offset-gray-800 transition ease-in-out duration-150"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            <!-- Summary Metrics -->
            <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center flex-1">
                            <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                                <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Active</p>
                                <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ metrics.active_count }}</p>
                            </div>
                        </div>
                        <InfoButton title="Active Incidents" variant="subtle" size="sm">
                            <p><strong>Active Incidents</strong> shows the total number of incidents with SLA policies that are currently not in closed status.</p>
                            <p class="mt-2">This includes incidents in statuses such as: open, triaged, under_investigation, and requires_review.</p>
                        </InfoButton>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center flex-1">
                            <div class="flex-shrink-0 bg-red-100 dark:bg-red-900/30 rounded-md p-3">
                                <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Breached</p>
                                <p class="text-2xl font-semibold text-red-600 dark:text-red-400">{{ metrics.breached_count }}</p>
                            </div>
                        </div>
                        <InfoButton title="Breached SLAs" variant="subtle" size="sm">
                            <p><strong>Breached SLAs</strong> shows incidents where the SLA response or investigation deadline has already passed.</p>
                            <p class="mt-2">An incident is marked as breached when:</p>
                            <ul class="list-disc ml-5 mt-1 space-y-1">
                                <li>Response time exceeded the SLA deadline</li>
                                <li>Investigation time exceeded the SLA deadline</li>
                            </ul>
                            <p class="mt-2">These require immediate attention to resolve.</p>
                        </InfoButton>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center flex-1">
                            <div class="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/30 rounded-md p-3">
                                <svg class="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">At Risk</p>
                                <p class="text-2xl font-semibold text-yellow-600 dark:text-yellow-400">{{ metrics.at_risk_count }}</p>
                            </div>
                        </div>
                        <InfoButton title="At Risk Incidents" variant="subtle" size="sm">
                            <p><strong>At Risk</strong> shows the combined count of incidents in Warning and Critical status.</p>
                            <p class="mt-2">This includes:</p>
                            <ul class="list-disc ml-5 mt-1 space-y-1">
                                <li><strong>Warning:</strong> 20-49% time remaining</li>
                                <li><strong>Critical:</strong> Less than 20% time remaining</li>
                            </ul>
                            <p class="mt-2">These incidents need priority attention to avoid SLA breaches.</p>
                        </InfoButton>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center flex-1">
                            <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                                <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Response</p>
                                <p class="text-2xl font-semibold text-green-600 dark:text-green-400">{{ metrics.response_percentage }}%</p>
                            </div>
                        </div>
                        <InfoButton title="Response SLA Performance" variant="subtle" size="sm">
                            <p><strong>Response SLA Performance</strong> shows the percentage of incidents where the response SLA was met within the deadline.</p>
                            <p class="mt-2">Based on closed incidents from the last 90 days.</p>
                            <p class="mt-2"><strong>Response SLA</strong> is the time allowed to triage and begin working on an incident after it's created.</p>
                        </InfoButton>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center flex-1">
                            <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                                <svg class="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Investigation</p>
                                <p class="text-2xl font-semibold text-purple-600 dark:text-purple-400">{{ metrics.investigation_percentage }}%</p>
                            </div>
                        </div>
                        <InfoButton title="Investigation SLA Performance" variant="subtle" size="sm">
                            <p><strong>Investigation SLA Performance</strong> shows the percentage of incidents where the investigation SLA was met within the deadline.</p>
                            <p class="mt-2">Based on closed incidents from the last 90 days.</p>
                            <p class="mt-2"><strong>Investigation SLA</strong> is the total time allowed to completely investigate and close an incident after it's created.</p>
                        </InfoButton>
                    </div>
                </div>
            </div>

            <!-- SLA Status Categories -->
            <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
                <!-- On Track Incidents -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">On Track ({{ onTrackIncidents.length }})</h3>
                            <InfoButton title="On Track Incidents" variant="subtle" size="sm">
                                <p><strong>On Track</strong> incidents have 50% or more time remaining before the SLA deadline.</p>
                                <p class="mt-2">These are progressing well and not at immediate risk of SLA breach.</p>
                            </InfoButton>
                        </div>
                        <p class="text-sm text-gray-500 dark:text-gray-400">50%+ time remaining</p>
                    </div>
                    <div class="p-6">
                        <div v-if="onTrackIncidents.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
                            <div v-for="incident in onTrackIncidents" :key="incident.id" class="border border-green-200 dark:border-green-800 rounded-lg p-4 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <Link :href="route('incidents.show', incident.id)" class="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                                            #{{ incident.incident_number }} - {{ incident.title }}
                                        </Link>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            <span v-if="incident.type">{{ incident.type.name }}</span>
                                            <span v-if="incident.location"> • {{ incident.location.name }}</span>
                                        </p>
                                        <div class="mt-2 flex flex-wrap gap-2 text-xs">
                                            <span v-if="incident.sla_response_due_at && !incident.sla_response_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                                Response: {{ formatTimeRemaining(incident.sla_response_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_investigation_due_at && !incident.sla_investigation_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                                Investigation: {{ formatTimeRemaining(incident.sla_investigation_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_policy" class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {{ incident.sla_policy.name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p class="mt-2">No on track incidents</p>
                        </div>
                    </div>
                </div>

                <!-- Warning Incidents -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Warning ({{ warningIncidents.length }})</h3>
                            <InfoButton title="Warning Incidents" variant="subtle" size="sm">
                                <p><strong>Warning</strong> incidents have between 20% and 49% time remaining before the SLA deadline.</p>
                                <p class="mt-2">These need increased attention to avoid potential SLA breaches.</p>
                            </InfoButton>
                        </div>
                        <p class="text-sm text-gray-500 dark:text-gray-400">20-49% time remaining</p>
                    </div>
                    <div class="p-6">
                        <div v-if="warningIncidents.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
                            <div v-for="incident in warningIncidents" :key="incident.id" class="border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <Link :href="route('incidents.show', incident.id)" class="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                                            #{{ incident.incident_number }} - {{ incident.title }}
                                        </Link>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            <span v-if="incident.type">{{ incident.type.name }}</span>
                                            <span v-if="incident.location"> • {{ incident.location.name }}</span>
                                        </p>
                                        <div class="mt-2 flex flex-wrap gap-2 text-xs">
                                            <span v-if="incident.sla_response_due_at && !incident.sla_response_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                                Response: {{ formatTimeRemaining(incident.sla_response_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_investigation_due_at && !incident.sla_investigation_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                                Investigation: {{ formatTimeRemaining(incident.sla_investigation_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_policy" class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {{ incident.sla_policy.name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            <svg class="mx-auto h-12 w-12 text-green-400 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p class="mt-2">No incidents in warning status</p>
                        </div>
                    </div>
                </div>

                <!-- Critical Incidents -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Critical ({{ criticalIncidents.length }})</h3>
                            <InfoButton title="Critical Incidents" variant="subtle" size="sm">
                                <p><strong>Critical</strong> incidents have less than 20% time remaining before the SLA deadline.</p>
                                <p class="mt-2">These require immediate priority attention to avoid SLA breaches.</p>
                            </InfoButton>
                        </div>
                        <p class="text-sm text-gray-500 dark:text-gray-400">&lt;20% time remaining</p>
                    </div>
                    <div class="p-6">
                        <div v-if="criticalIncidents.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
                            <div v-for="incident in criticalIncidents" :key="incident.id" class="border border-orange-200 dark:border-orange-800 rounded-lg p-4 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <Link :href="route('incidents.show', incident.id)" class="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                                            #{{ incident.incident_number }} - {{ incident.title }}
                                        </Link>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            <span v-if="incident.type">{{ incident.type.name }}</span>
                                            <span v-if="incident.location"> • {{ incident.location.name }}</span>
                                        </p>
                                        <div class="mt-2 flex flex-wrap gap-2 text-xs">
                                            <span v-if="incident.sla_response_due_at && !incident.sla_response_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                                Response: {{ formatTimeRemaining(incident.sla_response_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_investigation_due_at && !incident.sla_investigation_met_at" class="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                                                Investigation: {{ formatTimeRemaining(incident.sla_investigation_due_at) }}
                                            </span>
                                            <span v-if="incident.sla_policy" class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {{ incident.sla_policy.name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            <svg class="mx-auto h-12 w-12 text-green-400 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p class="mt-2">No incidents in critical status</p>
                        </div>
                    </div>
                </div>

                <!-- Breached Incidents -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Breached SLAs ({{ breachedIncidents.length }})</h3>
                            <InfoButton title="Breached Incidents" variant="subtle" size="sm">
                                <p>Incidents where the SLA deadline has already passed. These require immediate attention.</p>
                            </InfoButton>
                        </div>
                    </div>
                    <div class="p-6">
                        <div v-if="breachedIncidents.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
                            <div v-for="incident in breachedIncidents" :key="incident.id" class="border border-red-200 dark:border-red-800 rounded-lg p-4 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <Link :href="route('incidents.show', incident.id)" class="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                                            #{{ incident.incident_number }} - {{ incident.title }}
                                        </Link>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            <span v-if="incident.type">{{ incident.type.name }}</span>
                                            <span v-if="incident.location"> • {{ incident.location.name }}</span>
                                        </p>
                                        <div class="mt-2 flex flex-wrap gap-2 text-xs">
                                            <span v-if="incident.sla_response_breached" class="inline-flex items-center px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                                Response Breached
                                            </span>
                                            <span v-if="incident.sla_investigation_breached" class="inline-flex items-center px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                                Investigation Breached
                                            </span>
                                            <span v-if="incident.sla_policy" class="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {{ incident.sla_policy.name }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            <svg class="mx-auto h-12 w-12 text-green-400 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p class="mt-2">No breached SLAs</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Performance Breakdowns -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- By Severity -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Investigation Performance by Severity</h3>
                            <InfoButton title="Investigation Performance by Severity" variant="subtle" size="sm">
                                <p><strong>Performance by Severity</strong> shows how well your team is meeting SLA investigation deadlines for incidents at each severity level.</p>
                                <p class="mt-2">This helps identify if certain severity levels (Low, Medium, High, Critical) are consistently meeting or missing their SLA targets.</p>
                                <p class="mt-2"><strong>Based on:</strong> Incidents closed in the last 90 days</p>
                                <p class="mt-2"><strong>Percentage shown:</strong> Proportion of incidents where investigation SLA was met within the deadline</p>
                            </InfoButton>
                        </div>
                    </div>
                    <div class="p-6">
                        <div v-if="performanceBySeverity.length > 0" class="space-y-4">
                            <div v-for="item in performanceBySeverity" :key="item.label">
                                <div class="flex justify-between items-center mb-1">
                                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ item.label }}</span>
                                    <span class="text-sm text-gray-500 dark:text-gray-400">{{ item.percentage }}%</span>
                                </div>
                                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        class="h-2 rounded-full transition-all"
                                        :class="item.percentage >= 80 ? 'bg-green-500' : item.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'"
                                        :style="{ width: item.percentage + '%' }"
                                    ></div>
                                </div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.met }} of {{ item.total }} completed</p>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            No data available
                        </div>
                    </div>
                </div>

                <!-- By Type -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Investigation Performance by Type</h3>
                            <InfoButton title="Investigation Performance by Type" variant="subtle" size="sm">
                                <p><strong>Performance by Type</strong> shows how well your team is meeting SLA investigation deadlines for different incident types.</p>
                                <p class="mt-2">This helps identify which incident types are consistently meeting their SLA targets and which may need additional resources or process improvements.</p>
                                <p class="mt-2"><strong>Based on:</strong> Incidents closed in the last 90 days</p>
                            </InfoButton>
                        </div>
                    </div>
                    <div class="p-6">
                        <div v-if="performanceByType.length > 0" class="space-y-4">
                            <div v-for="item in performanceByType" :key="item.label">
                                <div class="flex justify-between items-center mb-1">
                                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ item.label }}</span>
                                    <span class="text-sm text-gray-500 dark:text-gray-400">{{ item.percentage }}%</span>
                                </div>
                                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        class="h-2 rounded-full transition-all"
                                        :class="item.percentage >= 80 ? 'bg-green-500' : item.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'"
                                        :style="{ width: item.percentage + '%' }"
                                    ></div>
                                </div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.met }} of {{ item.total }} completed</p>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            No data available
                        </div>
                    </div>
                </div>

                <!-- By Policy -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex items-center gap-2">
                            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Investigation Performance by Policy</h3>
                            <InfoButton title="Investigation Performance by Policy" variant="subtle" size="sm">
                                <p><strong>Performance by Policy</strong> shows how well your team is meeting the investigation deadlines for each SLA policy.</p>
                                <p class="mt-2">SLA policies define the response and investigation time targets for incidents. Different policies may be assigned based on incident type or near-miss status.</p>
                                <p class="mt-2">This breakdown helps identify which policies are consistently being met and which ones may need attention or adjustment.</p>
                                <p class="mt-2"><strong>Based on:</strong> Incidents closed in the last 90 days</p>
                                <p class="mt-2"><strong>Percentage shown:</strong> Proportion of incidents where investigation SLA was met within the deadline for each policy</p>
                            </InfoButton>
                        </div>
                    </div>
                    <div class="p-6">
                        <div v-if="performanceByPolicy.length > 0" class="space-y-4">
                            <div v-for="item in performanceByPolicy" :key="item.policy_name">
                                <div class="flex justify-between items-center mb-1">
                                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ item.policy_name }}</span>
                                    <span class="text-sm text-gray-500 dark:text-gray-400">{{ item.percentage }}%</span>
                                </div>
                                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        class="h-2 rounded-full transition-all"
                                        :class="item.percentage >= 80 ? 'bg-green-500' : item.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'"
                                        :style="{ width: item.percentage + '%' }"
                                    ></div>
                                </div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.met }} of {{ item.total }} completed</p>
                            </div>
                        </div>
                        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                            No data available
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { formatDistanceToNow } from 'date-fns';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    metrics: Object,
    breachedIncidents: Array,
    atRiskIncidents: Array,
    onTrackIncidents: Array,
    warningIncidents: Array,
    criticalIncidents: Array,
    performanceBySeverity: Array,
    performanceByType: Array,
    performanceByPolicy: Array,
});

const refreshDashboard = () => {
    router.post(route('incidents.sla-dashboard.refresh'), {}, {
        preserveScroll: true,
    });
};

const formatTimeRemaining = (dueAt) => {
    if (!dueAt) return '';
    return formatDistanceToNow(new Date(dueAt), { addSuffix: true });
};
</script>
